import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Chip,
  Fade,
  Zoom
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  SkipNext as SkipIcon
} from '@mui/icons-material';
import AppTheme from '../shared-theme/AppTheme';

const TourGuide = ({ 
  steps = [], 
  isOpen = false, 
  onClose, 
  onComplete,
  showProgress = true,
  showSkip = true,
  theme = 'light'
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [overlayStyle, setOverlayStyle] = useState({});
  const [targetElement, setTargetElement] = useState(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (isOpen && steps.length > 0) {
      updateTargetElement();
      
      // Add scroll listener to update position
      const handleScroll = () => {
        updateTargetElement();
      };
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [isOpen, currentStep, steps]);

  useEffect(() => {
    if (isOpen) {
      // Store original overflow value
      const originalOverflow = document.body.style.overflow;
      // Allow scrolling during tour
      document.body.style.overflow = 'auto';
      
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const updateTargetElement = () => {
    const step = steps[currentStep];
    if (step && step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        setTargetElement(element);
        const rect = element.getBoundingClientRect();
        setOverlayStyle({
          position: 'fixed',
          top: rect.top - 10,
          left: rect.left - 10,
          width: rect.width + 20,
          height: rect.height + 20,
          backgroundColor: 'rgba(33, 150, 243, 0.3)',
          border: '3px solid #2196F3',
          borderRadius: '8px',
          zIndex: 9998,
          pointerEvents: 'none',
          transition: 'all 0.3s ease-in-out'
        });
        
        // Auto-scroll to element if it's not visible
        const isElementVisible = rect.top >= 0 && rect.bottom <= window.innerHeight && 
                                rect.left >= 0 && rect.right <= window.innerWidth;
        
        if (!isElementVisible) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
        }
      }
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep(0);
    setTargetElement(null);
    setOverlayStyle({});
    if (onClose) {
      onClose();
    }
  };

  const getTooltipPosition = () => {
    if (!targetElement) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    
    const rect = targetElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = 400;
    const tooltipHeight = 300;
    
    // Check if element is visible in viewport
    const isElementVisible = rect.top >= 0 && rect.bottom <= viewportHeight && 
                            rect.left >= 0 && rect.right <= viewportWidth;
    
    // If element is not visible, center the tooltip
    if (!isElementVisible) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999
      };
    }
    
    // Calculate optimal position
    let top = rect.bottom + 20;
    let left = rect.left;
    let transform = 'none';
    
    // Adjust if too close to bottom
    if (top + tooltipHeight > viewportHeight) {
      top = rect.top - tooltipHeight - 20;
    }
    
    // Adjust if too close to right edge
    if (left + tooltipWidth > viewportWidth) {
      left = viewportWidth - tooltipWidth - 20;
    }
    
    // Adjust if too close to left edge
    if (left < 20) {
      left = 20;
    }
    
    return {
      position: 'fixed',
      top: `${Math.max(20, top)}px`,
      left: `${Math.max(20, left)}px`,
      zIndex: 9999,
      transform
    };
  };

  if (!isOpen || steps.length === 0) return null;

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <AppTheme>
      {/* Overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9997,
          pointerEvents: 'none' // Allow scrolling and interaction
        }}
      >
        {/* Close button in top-right corner */}
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            pointerEvents: 'auto'
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#000',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Target Highlight */}
      {targetElement && (
        <Box
          ref={overlayRef}
          sx={overlayStyle}
        />
      )}

      {/* Tooltip */}
      <Fade in={isOpen} timeout={300}>
        <Paper
          elevation={24}
          sx={{
            ...getTooltipPosition(),
            maxWidth: 400,
            minWidth: 300,
            backgroundColor: theme === 'dark' ? '#424242' : '#ffffff',
            color: theme === 'dark' ? '#ffffff' : '#000000',
            borderRadius: 2,
            p: 3
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showProgress && (
                <Chip
                  label={`${currentStep + 1} / ${steps.length}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              <Typography variant="h6" fontWeight="bold">
                {currentStepData.title || 'Tour Guide'}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            {currentStepData.content}
          </Typography>

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              {showSkip && (
                <Button
                  onClick={handleSkip}
                  color="inherit"
                  startIcon={<SkipIcon />}
                  sx={{ mr: 1 }}
                >
                  Saltar
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {currentStep > 0 && (
                <Button
                  onClick={handlePrevious}
                  variant="outlined"
                  startIcon={<PrevIcon />}
                >
                  Anterior
                </Button>
              )}
              
              <Button
                onClick={handleNext}
                variant="contained"
                endIcon={currentStep === steps.length - 1 ? null : <NextIcon />}
                color="primary"
              >
                {currentStep === steps.length - 1 ? 'Finalizar' : 'Siguiente'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </AppTheme>
  );
};

export default TourGuide;
